import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { Event, Log, getEvent, getEventLogs, cancelEvent } from '../services/api';

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId) return;
      
      setLoading(true);
      try {
        const [eventData, logsData] = await Promise.all([
          getEvent(eventId),
          getEventLogs(eventId)
        ]);
        
        setEvent(eventData);
        setLogs(logsData);
        setError('');
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Failed to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEventData();
  }, [eventId]);
  
  const handleCancelEvent = async () => {
    if (!eventId) return;
    
    setCancelling(true);
    try {
      await cancelEvent(eventId);
      // Refresh event data
      const eventData = await getEvent(eventId);
      setEvent(eventData);
    } catch (err) {
      console.error('Error cancelling event:', err);
      setError('Failed to cancel event. Please try again later.');
    } finally {
      setCancelling(false);
    }
  };
  
  const getStatusColor = (status: string) => {
    switch(status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'booked':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'primary';
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!event) {
    return (
      <Box>
        <Alert severity="error">Event not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 3 }}
      >
        Back to Events
      </Button>
      
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h5" component="div">
                  Event Details
                </Typography>
                <Chip 
                  label={event.status} 
                  color={getStatusColor(event.status)}
                />
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>External Event ID:</strong> {event.external_event_id}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Created:</strong> {formatDate(event.created_at)}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Last updated:</strong> {formatDate(event.updated_at)}
              </Typography>
              
              {event.status === 'pending' && (
                <Button 
                  variant="contained"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEvent}
                  disabled={cancelling}
                  fullWidth
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Event'}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="div" sx={{ mb: 2 }}>
                Event Logs
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              {logs.length === 0 ? (
                <Typography color="text.secondary">No logs available</Typography>
              ) : (
                <Box>
                  {logs.map((log) => (
                    <Paper key={log.id} elevation={1} sx={{ mb: 2, p: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {formatDate(log.created_at)}
                      </Typography>
                      <Typography variant="body1">
                        {log.message}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EventDetailPage; 